/**
 * La verificación de las fotos obligatorias contra S3 al cerrar (`POST storage/files/check`).
 *
 * El teléfono sabe qué archivos subió en este intento: cada visita a `/start` borra los datos locales,
 * así que no hereda los del intento anterior. Se pregunta por esos nombres exactos, no por un conteo.
 *
 * Cualquier cosa que no sea una respuesta completa y creíble cuenta como «no disponible», y entonces
 * el cierre decide con el teléfono, como antes de esta verificación. Bloquear de más deja al asegurado
 * sin poder cerrar, así que la duda nunca bloquea.
 */

export const STORAGE_CHECK_ENDPOINT = 'storage/files/check';

/**
 * El nombre que arma el Storage Manager: `let-pwa_<photo_id>_<uuid>.<ext>`. Es copia de
 * `KEY_PATH_PATTERN` del API interno, y `verify:storage-check` falla si las dos dejan de coincidir:
 * el servidor rechaza con 400 todo el pedido si un nombre no pasa su regex.
 */
export const STORAGE_FILE_NAME_PATTERN = /^let-pwa_(\d{1,9})_[0-9a-f-]{36}\.\w+$/;

/**
 * El nombre del archivo en S3, sacado de lo que el teléfono guardó al subirlo: la `publicUrl`, que es
 * `URL_AWS` más la key, y la key es `<carpeta>/i<OI>/<nombre>`. Lo que no tenga esa forma no se puede
 * verificar y devuelve `null`, y eso incluye un archivo en otra carpeta: el servidor solo busca bajo
 * `i<OI>`, así que uno subido por un Storage Manager que usaba otra carpeta saldría como ausente sin
 * estarlo.
 */
export function storageFileNameOf(data: unknown): string | null {
  if (typeof data !== 'string') {
    return null;
  }
  const segments = data.split(/[?#]/)[0].split('/');
  const name = segments[segments.length - 1];
  const folder = segments[segments.length - 2] ?? '';
  return /^i\d+$/.test(folder) && STORAGE_FILE_NAME_PATTERN.test(name) ? name : null;
}

/** El Storage Manager acepta hasta 100 nombres por pedido. Una OI con más archivos se reparte en varios pedidos. */
export const STORAGE_CHECK_MAX_NAMES = 100;

export function splitStorageCheckNames(names: readonly string[], max: number = STORAGE_CHECK_MAX_NAMES): string[][] {
  const unique = [...new Set(names)];
  const parts: string[][] = [];
  for (let i = 0; i < unique.length; i += max) {
    parts.push(unique.slice(i, i + max));
  }
  return parts;
}

export type StorageCheckOutcome =
  | { kind: 'ok'; exists: ReadonlyMap<string, boolean> }
  | { kind: 'unavailable'; detail: string };

/** `httpStatus` es `null` cuando no hubo respuesta: red caída, timeout o un fallo armando las cabeceras. */
export function classifyStorageCheck(httpStatus: number | null, body: unknown, asked: readonly string[]): StorageCheckOutcome {
  if (httpStatus === null) {
    return { kind: 'unavailable', detail: 'sin respuesta' };
  }
  if (httpStatus !== 200) {
    return { kind: 'unavailable', detail: `HTTP ${httpStatus}` };
  }
  const results = (body as { results?: unknown } | null)?.results;
  if (!Array.isArray(results)) {
    return { kind: 'unavailable', detail: 'respuesta sin results' };
  }
  const exists = new Map<string, boolean>();
  for (const item of results) {
    const { name, exists: found } = (item ?? {}) as { name?: unknown; exists?: unknown };
    if (typeof name !== 'string' || typeof found !== 'boolean' || exists.has(name)) {
      return { kind: 'unavailable', detail: 'respuesta mal formada' };
    }
    exists.set(name, found);
  }
  if (exists.size !== new Set(asked).size || asked.some((name) => !exists.has(name))) {
    return { kind: 'unavailable', detail: 'respuesta incompleta' };
  }
  // Que no exista ninguno apunta a una causa del sistema —un permiso, una carpeta, un ambiente—, no a
  // que el asegurado haya perdido todas sus fotos. El servidor ya lo responde como 503; esto es por si no.
  if (asked.length > 0 && [...exists.values()].every((found) => !found)) {
    return { kind: 'unavailable', detail: 'ninguno existe' };
  }
  return { kind: 'ok', exists };
}

/**
 * La respuesta de un pedido repartido. Si una parte no está disponible, no lo está el conjunto: con una
 * parte sin confirmar no se puede saber si falta algo, y la duda nunca bloquea.
 */
export function mergeStorageChecks(outcomes: readonly StorageCheckOutcome[]): StorageCheckOutcome {
  const exists = new Map<string, boolean>();
  for (const outcome of outcomes) {
    if (outcome.kind === 'unavailable') {
      return outcome;
    }
    outcome.exists.forEach((found, name) => exists.set(name, found));
  }
  return { kind: 'ok', exists };
}
